<script setup lang="ts">
import { IModal } from '@inkline/inkline'
import ListBar from '../global/ListBar.vue'
import Icon from '~/components/global/Icon.vue'
import CustomOfferForm from '~/components/forms/CustomOfferForm.vue'
import type { EstateDetailInterface } from '~/types/api/EstateDetail'
import useCustomOfferModalStore from '~/stores/modals/useCustomOfferModal'

const props = defineProps<{
  estateData: EstateDetailInterface
}>()

const customOfferModalStore = useCustomOfferModalStore()
const estateType = props.estateData.type
</script>

<template>
  <IModal
    :hide-on-click-outside="false"
    :name="customOfferModalStore.id"
    :model-value="customOfferModalStore.isOpened"
    @close="customOfferModalStore.onClose()"
    @open="customOfferModalStore.onOpen()"
  >
    <template #header>{{ $t('modals.customOffer.title') }}</template>
    <template #close><Icon name="close" /></template>

    <div class="c-modal-body">
      <div class="c-modal-body__representative">
        <img
          v-if="estateData.contact_person.image"
          class="sc-image"
          :src="estateData.contact_person.image"
          :alt="estateData.contact_person.name"
        />

        <div class="sc-info">
          <strong>{{ estateData.contact_person.name }}</strong>

          <a
            v-if="estateData.contact_person.phone"
            class="c-link c-link"
            :href="`tel:${estateData.contact_person.phone}`"
            >{{ estateData.contact_person.phone }}</a
          >
        </div>
      </div>

      <p
        class="c-modal-body__text"
        v-html="
          $t('modals.customOffer.text', {
            _0: '<strong>',
            _1: '</strong>',
          })
        "
      />

      <CustomOfferForm
        :estate-data="estateData"
        :modal-store="customOfferModalStore"
      />
    </div>

    <template #footer>
      <div class="c-footer">
        <strong class="c-footer__title">
          {{ $t('pages.estateDetail.representative.listTitle') }}
        </strong>

        <ListBar class="c-footer__list-bar">
          <li v-for="i in estateType === 'office' ? 4 : 3" :key="i">
            {{
              $t(`pages.estateDetail.representative.${estateType}.listItem${i}`)
            }}
          </li>
        </ListBar>
      </div>
    </template>
  </IModal>
</template>

<style lang="scss" scoped>
@use '@inkline/inkline/css/mixins' as *;

.c-modal-body {
  max-width: 360px;
  margin: auto;

  &__representative {
    display: flex;
    justify-content: center;
    gap: 16px;
    margin-bottom: 32px;

    .sc-image {
      width: 80px;
      height: 80px;
      border-radius: 50%;
      object-fit: cover;
    }

    .sc-info {
      display: flex;
      flex-direction: column;
      justify-content: center;
      align-items: flex-start;
      gap: 2px;
      font-size: 22px;
      line-height: 1.2;

      .c-link {
        font-size: 16px;
        font-weight: 600;
      }
    }
  }

  &__text {
    text-align: center;
    line-height: 1.28;
    margin-bottom: 32px;
    padding-bottom: 32px;
    border-bottom: 1px solid var(--color-border);
  }
}

.c-footer {
  display: flex;
  flex-direction: column;
  padding-top: 32px;
  border-top: 1px solid var(--color-border);

  &__title {
    margin-bottom: 14px;
  }

  &__list-bar {
    --c-list-bar--padding: 0;

    display: grid;
    grid-template-columns: repeat(2, auto);
    gap: 18px;

    @include breakpoint-down('xs') {
      grid-template-columns: 1fr;
    }
  }
}
</style>
